import React, { useState } from 'react';
import Button from '../../../components/UI/Button';
import Input from '../../../components/UI/Input';
import Select from '../../../components/UI/Select';
import { ContactFormData, ProjectType, ValidationErrors, FormSubmissionState } from '../types';
import Icon from '../../../components/AppIcon';

interface ContactFormSectionProps {
  onSubmit: (data: ContactFormData) => void;
  submissionState: FormSubmissionState;
}

const ContactFormSection = ({ onSubmit, submissionState }: ContactFormSectionProps) => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    company: '',
    projectType: '',
    message: '',
    phone: ''
  });

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [characterCount, setCharacterCount] = useState(0);

  const projectTypes: ProjectType[] = [
    { value: 'freelance', label: 'Freelance Project', description: 'Short-term execution' },
    { value: 'fulltime', label: 'Full-time Position', description: 'Long-term enterprise integration' },
    { value: 'consultation', label: 'Consultation', description: 'Strategic architectural advisory' },
    { value: 'collaboration', label: 'Collaboration', description: 'Special ops / Joint venture' },
    { value: 'other', label: 'Other', description: 'General transmission' }
  ];

  const validateField = (name: string, value: string): string | null => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Identity verification failed: Name required';
        if (value.length < 2) return 'Identity incomplete: Min 2 characters';
        return null;
      case 'email':
        if (!value.trim()) return 'Communication channel required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Invalid protocol: Check email syntax';
        return null;
      case 'projectType':
        if (!value) return 'Classification required';
        return null;
      case 'message':
        if (!value.trim()) return 'Packet content required';
        if (value.length < 10) return 'Content too brief: Min 10 characters';
        return null;
      default:
        return null;
    }
  };

  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'message') setCharacterCount(value.length);
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;
    Object.keys(formData).forEach(key => {
      if (key === 'phone' || key === 'company') return;
      const error = validateField(key, formData[key as keyof ContactFormData] || '');
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) onSubmit(formData);
  };

  const handleReset = () => {
    setFormData({ name: '', email: '', company: '', projectType: '', message: '', phone: '' });
    setErrors({});
    setCharacterCount(0);
  };

  if (submissionState.isSubmitted) {
    return (
      <div className="neubrutal-card bg-white p-12 text-center relative overflow-hidden group">
        <div className="absolute inset-0 z-0 opacity-[0.02] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(#0C2C39 0.5px, transparent 0.5px)',
            backgroundSize: '24px 24px'
          }} />

        <div className="relative z-10">
          <div className="w-20 h-20 bg-teal-700/10 border-2 border-teal-700 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-[4px_4px_0px_0px_rgba(13,148,136,0.2)]">
            <Icon name="Check" size={40} className="text-teal-700" />
          </div>
          <h3 className="text-3xl font-black text-[#0C2C39] mb-4 tracking-tight">Transmission Received</h3>
          <p className="text-[#0C2C39]/50 mb-10 max-w-sm mx-auto font-medium">
            Project parameters acknowledged. Expect synchronization within 24-48 hours.
          </p>
          <button
            onClick={handleReset}
            className="neubrutal-btn px-10 py-5 bg-[#0C2C39] text-white font-black uppercase tracking-widest text-xs rounded-xl shadow-[4px_4px_0px_0px_rgba(13,148,136,0.3)]"
          >
            New Transmission
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="neubrutal-card bg-white p-8 lg:p-12 relative overflow-hidden">
      {/* Subtle Pattern Grid */}
      <div className="absolute inset-0 z-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(#0C2C39 0.5px, transparent 0.5px)',
          backgroundSize: '24px 24px'
        }} />

      <div className="mb-12 relative z-10">
        <h2 className="text-4xl font-black text-[#0C2C39] mb-3 tracking-tighter font-roboto">Secure Uplink</h2>
        <p className="text-[#0C2C39]/50 text-lg font-medium italic">
          Synchronize your objectives with my development pipeline.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-10 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Input
            label="Designation / Name"
            placeholder="Identity Required"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={errors.name}
            required
          />

          <Input
            label="Protocol / Email"
            type="email"
            placeholder="comm-channel@domain.io"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={errors.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Input
            label="Organization"
            placeholder="Enterprise Name (Opt)"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
          />

          <Input
            label="Direct Line"
            type="tel"
            placeholder="+00 (000) 000-0000 (Opt)"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            error={errors.phone}
          />
        </div>

        <Select
          label="Engagement Type"
          placeholder="Classify Objective"
          options={projectTypes}
          value={formData.projectType}
          onChange={(value) => handleInputChange('projectType', value as string)}
          error={errors.projectType}
          required
        />

        <div className="space-y-4">
          <label className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0C2C39]/40 block mb-2">
            Transmission Data <span className="text-red-700">*</span>
          </label>
          <textarea
            placeholder="Outline project specifications, technical requirements, and strategic goals..."
            value={formData.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            rows={6}
            maxLength={1000}
            className={`w-full px-5 py-4 border-2 rounded-xl bg-white text-[#0C2C39] font-medium resize-none transition-all focus:outline-none focus:shadow-[3px_3px_0px_0px_#0C2C39] placeholder:text-[#0C2C39]/20 ${errors.message ? 'border-red-700' : 'border-[#0C2C39]'
              }`}
          />
          <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-widest pt-2">
            <div className="text-red-700">{errors.message}</div>
            <div className={characterCount > 900 ? 'text-amber-600' : 'text-[#0C2C39]/20'}>
              {characterCount} / 1000 Payload Units
            </div>
          </div>
        </div>

        {submissionState.error && (
          <div className="p-5 bg-red-700/5 border-2 border-red-700/20 rounded-xl">
            <p className="text-red-700 text-xs font-black uppercase tracking-widest">{submissionState.error}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-6 pt-4">
          <button
            type="submit"
            disabled={submissionState.isSubmitting}
            className="flex-1 neubrutal-btn px-10 py-5 bg-[#0C2C39] text-white font-black uppercase tracking-widest text-xs rounded-xl shadow-[4px_4px_0px_0px_rgba(13,148,136,0.3)] disabled:opacity-50 disabled:shadow-none flex items-center justify-center group"
          >
            {submissionState.isSubmitting ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                Initialize Synchronization
                <Icon name="ArrowRight" size={16} className="ml-3 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>

          <button
            type="button"
            onClick={handleReset}
            disabled={submissionState.isSubmitting}
            className="sm:w-auto px-8 py-5 bg-white border-2 border-[#0C2C39] text-[#0C2C39] font-black uppercase tracking-widest text-[10px] rounded-xl hover:bg-[#0C2C39]/5 transition-colors"
          >
            Clear Buffer
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactFormSection;